import React, { type JSX } from 'react'
import dayjs from 'dayjs'
import { useLocation, useParams } from 'react-router'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { Button } from '@mobi/component-library/Base/Buttons'
import { colors } from '@mobi/component-library/Foundations'
import { Spinner } from '@mobi/component-library/Legacy/Spinner'
import { PageHeader } from '@mobi/component-library/Navigation/PageHeader'
import { TabButton, Tabs } from '@mobi/component-library/Navigation/Tabs'
import { iotSubscribeTopics, iotUnsubscribeTopics } from '@mobi/utils/awsiot'
import { useMysteryBetDetails } from '@core/Areas/MysteryBet/Hooks/useMysteryBetDetails'
import { MainContainer } from '@core/Components/Containers'
import { NoticeBox } from '@core/Components/NoticeBox'
import {
  ToteRaceAbandonedPushEvent,
  ToteRaceStatusChangedPushEvent,
  getToteRaceTopic,
} from '@core/State/PushData'
import { fetchCampaignsAsync } from '@core/State/UserAccount/async-signals'
import { useLogon, useObservableImmutable } from '@core/Utils/hooks'
import { useAppSeo } from '@core/Utils/hooks/useAppSeo'
import { appHistory } from '@core/Utils/routing/history'
import { NextRaceDrawer } from './Components/Common/NextRaceDrawer'
import { CustomizeBet } from './Components/CustomizeBet'
import { MeetingInformation } from './Components/MeetingInformation'
import { QuickPick } from './Components/QuickPick'
import { SetGoToMeeting, SetGoToNextRace, SetIsRaceClosed, SetMysteryData, state$ } from './driver'

const NEXT_RACE_PATH = '/tote/mystery'

export const MysteryBetPage = (): JSX.Element | null => {
  useAppSeo({
    title: 'Mystery Odds & Racing Markets | TABtouch',
    description:
      'Explore mystery betting odds, race markets, form guides and racing insights with TABtouch.',
  })
  const location = useLocation()
  const { meetingId, raceNumber } = useParams<{ meetingId: string; raceNumber: string }>()
  const date = new URLSearchParams(location.search).get('date') || undefined
  const { isRaceClosed, isBetConfirmed } = useObservableImmutable(state$, [
    'isRaceClosed',
    'isBetConfirmed',
  ])
  const { isLoggedIn } = useLogon()

  const [currentTab, setCurrentTab] = React.useState<'Quick Pick' | 'Customise Bet'>('Quick Pick')

  const isForSpecificRace = !!meetingId && !!raceNumber && !!date

  const { isError, data, isFetching, refetch } = useMysteryBetDetails({
    meetingId,
    date,
    raceNumber: raceNumber ? +raceNumber : undefined,
  })

  React.useEffect(() => {
    SetMysteryData(data)
  }, [data])

  React.useEffect(() => {
    if (isLoggedIn) {
      fetchCampaignsAsync()
    }
  }, [isLoggedIn])

  React.useEffect(() => {
    SetIsRaceClosed(data?.Race != null && data.Race.RaceStatusText !== 'Open')
    const raceKey = data?.RaceKeyForLiveUpdates
    let subscription: Rx.IDisposable
    if (raceKey) {
      subscription = iotSubscribeTopics(getTopics(raceKey)).subscribe(e => {
        if (e.topic?.includes(raceKey)) {
          if (e.payload?.eventType === 'ToteRaceStatusChanged') {
            const statusChangedPayload = e.payload as ToteRaceStatusChangedPushEvent
            SetIsRaceClosed(statusChangedPayload.status !== 'open')
          } else if (e.payload?.eventType === 'ToteRaceAbandonChanged') {
            const abandonChangedPayload = e.payload as ToteRaceAbandonedPushEvent
            if (abandonChangedPayload.status !== 'unabandoned') {
              SetIsRaceClosed(true)
            }
          }
        }
      })
    }
    return () => {
      subscription?.dispose()
      if (raceKey) iotUnsubscribeTopics(getTopics(raceKey))
    }
  }, [data])

  const formattedDate = dayjs(data?.Meeting?.MeetingDate).format('YYYY-MM-DD')
  const goToNextRace = React.useCallback(
    () => (location.pathname === NEXT_RACE_PATH ? refetch() : appHistory.push(NEXT_RACE_PATH)),
    [location.pathname, refetch]
  )
  const goToMeeting = React.useCallback(
    () => appHistory.push(`/tote/meetings/${data?.Meeting?.MeetingId}?date=${formattedDate}`),
    [data?.Meeting?.MeetingId, formattedDate]
  )
  SetGoToNextRace(goToNextRace)
  SetGoToMeeting(goToMeeting)

  if (isFetching) {
    return (
      <div data-testid='Spinner'>
        <Spinner />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <MainContainer data-tid-blackbook background='surface300'>
        <PageHeader title='Mystery Bet' />
        <NoticeBox
          title='Unable to place a mystery bet at this time. Please try again later.'
          noticeBoxType='Error'
        />
      </MainContainer>
    )
  }

  return (
    <MainContainer data-tid-blackbook background='surface300'>
      <PageHeader
        title='Mystery Bet'
        rightAlignedComponent={
          <Button
            size='sm'
            isIconOnlyButton
            color='tertiary_dark'
            disabled={isFetching}
            onClick={() => !isForSpecificRace && refetch()}
          >
            <Icon name='LineRefreshCw05' color={colors.white} />
          </Button>
        }
      />

      <MeetingInformation />

      <Tabs bottomBorder='hard'>
        <TabButton
          content='Quick Pick'
          isSelected={currentTab === 'Quick Pick'}
          onClick={() => setCurrentTab('Quick Pick')}
          testId='Quick Pick'
        />

        <TabButton
          content='Customise Bet'
          isSelected={currentTab === 'Customise Bet'}
          onClick={() => setCurrentTab('Customise Bet')}
          testId='Customise Bet'
        />
      </Tabs>

      {currentTab === 'Quick Pick' && <QuickPick />}
      {currentTab === 'Customise Bet' && <CustomizeBet />}

      {isRaceClosed && !isBetConfirmed && (
        <NextRaceDrawer
          isOpen
          onGoToMeeting={goToMeeting}
          onNextRace={goToNextRace}
          shouldDisplayAboveTabNav
        />
      )}
    </MainContainer>
  )
}

// =============
// Local Helpers
// =============

function getTopics(raceKey: string): string[] {
  return [
    getToteRaceTopic(raceKey, { type: 'statusChanged' }),
    getToteRaceTopic(raceKey, { type: 'abandonedChanged' }),
  ]
}
